import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import * as api from '../api.ts';
import { Channels } from './channel-setup.tsx';
import { OrgConfirmation } from './org-confirmation.tsx';
import {
  Button,
  Card,
  Cell,
  Empty,
  Field,
  Finding,
  Label,
  ListRow,
  Metric,
  Metrics,
  Mono,
  PageTitle,
  Row,
  Section,
  Select,
  Spinner,
  Status,
  Table,
  sentence,
} from '../ui.tsx';
import type { Screen } from '../shell.tsx';
import { WorkMobileVerify, workMobileHelp } from '../work-mobile.tsx';

/**
 * One question: **change one thing safely.**
 *
 * Plain forms, no wizard. A wizard is for a first-run flow somebody does once;
 * settings are changed by somebody who already knows what they came for.
 */

const TEMPLATE_KINDS = ['alert', 'all_clear', 'correction'] as const;

/**
 * Stand-down and correction templates — R3, and the reason it is a settings
 * screen rather than an incident screen.
 *
 * Hawaii's correction took 38 minutes because staff hand-built the corrective
 * message live. R3 makes the templates a precondition of going live at all
 * (migration 0011's qualification checks them), and until this section existed
 * the dashboard could not author one — an org could qualify for live only by
 * someone driving the API from a shell.
 */
/**
 * What the recipient actually receives, beside the box it is typed into.
 *
 * Apple Business Manager puts the live place card next to every Brands form
 * (`design-dashboard.md` §7c). The reason it matters more here than there: this
 * is the message R3 exists for — the one Hawaii spent thirty-eight minutes
 * composing during the incident — and until now it was written blind.
 *
 * **Every line is transcribed from `api/src/delivery/index.ts`, not invented.**
 * A preview that differs from what is sent is worse than no preview, so if that
 * file changes, this has to change with it. The parts the delivery layer fills
 * in are rendered muted and labelled, because the whole point is to show which
 * words are the author's and which are not.
 */
function Preview({ kind, body }: { kind: string; body: string }) {
  const auto = (t: string) => <span className="text-[var(--ink-3)]">{t}</span>;

  if (kind === 'alert') {
    return (
      <div className="rounded-[var(--radius-2)] border border-[var(--line)] bg-[var(--paper)] p-4">
        <p className="text-[13px] leading-[18px] text-[var(--ink-2)]">
          Nothing sends this. An emergency's words are typed at the moment it is raised, and every
          channel uses those — so an alert template is stored and never read. The all-clear and the
          correction are the two that are authored in advance, and they are the two that have to be.
        </p>
      </div>
    );
  }

  const correction = kind === 'correction';
  return (
    <div className="overflow-hidden rounded-[var(--radius-2)] border border-[var(--line)] bg-[var(--paper)]">
      <div className="border-b border-[var(--line)] px-4 py-2.5 text-[13px]">
        <span className="text-[var(--ink-3)]">Subject </span>
        <span className="font-medium">{correction ? 'SENT IN ERROR' : 'ALL CLEAR'} — </span>
        {auto('the site’s name')}
      </div>
      <div className="px-4 py-3 text-[14px] leading-[21px] whitespace-pre-line">
        {correction ? 'The previous message was sent in error.' : 'The emergency is over.'}
        {'\n\n'}
        {auto('The site’s name — the area')}
        {'\n'}
        <span className="font-medium">{body || auto('your message')}</span>
        {'\n\n'}
        {correction
          ? 'There is no emergency. We are sorry for the alarm.'
          : 'Follow any instructions from wardens or emergency services on site.'}
      </div>
    </div>
  );
}

function Templates({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const [capEvent, setCapEvent] = useState('Evacuation');
  const [kind, setKind] = useState<(typeof TEMPLATE_KINDS)[number]>('all_clear');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  const templates = useQuery({
    queryKey: ['templates', orgId],
    queryFn: () => api.getTemplates(orgId),
  });

  const [translated, setTranslated] = useState<string | null>(null);
  const translate = useMutation({
    mutationFn: () => api.translateTemplates(orgId),
    onSuccess: (res) => {
      if (!res) return;
      setTranslated(
        res.translated.length === 0 && res.failed.length === 0
          ? 'Every declared language already has every template.'
          : `${res.translated.length} translated${
              res.failed.length ? `, ${res.failed.length} failed` : ''
            }. Have a speaker check the words before anyone depends on them.`,
      );
      void qc.invalidateQueries({ queryKey: ['templates', orgId] });
    },
    onError: (e: Error) => setTranslated(e.message),
  });

  const add = useMutation({
    mutationFn: () => api.addTemplate(orgId, { cap_event: capEvent, kind, locale: 'en-AU', body }),
    onSuccess: () => {
      setBody('');
      setError(null);
      void qc.invalidateQueries({ queryKey: ['templates', orgId] });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Section title="Message templates">
      <p className="mb-4 max-w-[640px] text-[14px] text-[var(--ink-2)]">
        Written now, on a calm afternoon, so nobody composes the all-clear during an emergency. A
        stand-down template for each alert type is required before this organisation can go live —
        Hawaii took 38 minutes to correct a false alarm because staff had to write the message while
        it was out.
      </p>

      {templates.isLoading || !templates.data ? (
        <Spinner />
      ) : templates.data.templates.length === 0 ? (
        <Empty>No templates yet. The all-clear for “Evacuation” is the one to write first.</Empty>
      ) : (
        <Table head={['Alert type', 'Message kind', 'Language', 'Text']}>
          {templates.data.templates.map((t) => (
            <Row key={`${t.cap_event}-${t.kind}-${t.locale}`}>
              <Cell>{t.cap_event}</Cell>
              <Cell>{sentence(t.kind.replace(/_/g, ' '))}</Cell>
              <Cell>{t.locale}</Cell>
              <Cell wide>{t.body}</Cell>
            </Row>
          ))}
        </Table>
      )}

      {templates.data && templates.data.templates.length > 0 ? (
        <div className="mt-4">
          {/* Translated when authored, never during an incident: the whole
              point of the template path (migration 0016). Idempotent — a
              block a person wrote is never overwritten. */}
          <Button
            variant="secondary"
            disabled={translate.isPending}
            label="Translate templates into every declared language"
            onClick={() => translate.mutate()}
          >
            Translate into declared languages
          </Button>
          {translated ? (
            <div className="mt-3">
              <Finding tone="neutral">{translated}</Finding>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mt-6 max-w-[640px] border-t border-[var(--line)] pt-5">
        <div className="flex flex-wrap gap-3">
          <div className="w-[220px]">
            <Field
              label="Alert type"
              value={capEvent}
              onChange={setCapEvent}
              placeholder="Evacuation"
            />
          </div>
          <div className="w-[200px]">
            <Select
              label="Message kind"
              value={kind}
              onChange={(v) => setKind(v as (typeof TEMPLATE_KINDS)[number])}
              options={TEMPLATE_KINDS.map((k) => ({
                value: k,
                label: sentence(k.replace(/_/g, ' ')),
              }))}
            />
          </div>
        </div>
        {/*
          The old example read "The emergency at {site} is over" — and nothing
          in the codebase substitutes {site}, so an admin following it sent the
          braces, verbatim, at the moment people were deciding whether to walk
          back into a building. It was unnecessary as well as broken: the
          delivery layer already puts the site's name in the subject and on its
          own line above this text.
        */}
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <div>
            <Field
              label="Message"
              value={body}
              onChange={setBody}
              placeholder="It is safe to return."
              hint="Sent exactly as written. The site and area are added for you."
            />
          </div>
          <div>
            <div className="mb-1.5">
              <Label>What arrives</Label>
            </div>
            <Preview kind={kind} body={body} />
          </div>
        </div>
        {error ? (
          <div className="mt-3">
            <Finding>{error}</Finding>
          </div>
        ) : null}
        <div className="mt-4">
          <Button
            variant="primary"
            disabled={!body || !capEvent || add.isPending}
            onClick={() => add.mutate()}
          >
            Save template
          </Button>
        </div>
      </div>
    </Section>
  );
}

/**
 * The deep-link target carried in the Settings URL hash, e.g.
 * `#golive-drillCompleted` → open the Going-live category and focus the
 * `drillCompleted` step. Parsed once on mount; the hash before the first `-` is
 * the category, the rest is the step. Wrapped in try/catch like every other
 * `window` touch so a locked-down environment falls back to the default landing.
 */
function readSettingsHashTarget(): { category: string | null; step: string | null } {
  try {
    const raw = window.location.hash.replace(/^#/, '');
    if (!raw) return { category: null, step: null };
    const dash = raw.indexOf('-');
    return dash === -1
      ? { category: raw, step: null }
      : { category: raw.slice(0, dash), step: raw.slice(dash + 1) };
  } catch {
    return { category: null, step: null };
  }
}

export function Settings({
  orgId,
  orgName,
  onNavigate,
}: {
  orgId: string;
  orgName: string;
  /** Settings is org-level config plus the hub for off-rail records — the
   * readiness report, the Activity record and the evidence workspace reopen
   * here. Locations is no longer here: it is its own rail directory. */
  onNavigate: (screen: Screen) => void;
}) {
  const ent = useQuery({
    queryKey: ['entitlements', orgId],
    queryFn: () => api.getEntitlements(orgId),
  });

  /**
   * Both of these leave the app for Stripe, and both used to fail in
   * silence: `onSuccess` navigated and nothing handled the other case, so a
   * refused request produced a button that did nothing at all. The most
   * likely refusal is the freshness gate — checkout and the portal are
   * administration, so they need a session minted in the last few minutes
   * — and "nothing happened" is the worst possible way to say
   * "sign in again".
   */
  const [billingError, setBillingError] = useState<string | null>(null);

  const explain = (err: unknown): string =>
    err instanceof api.ApiError && err.status === 403
      ? 'For your security this needs a fresh sign-in. Sign out and back in, then try again.'
      : err instanceof api.ApiError
        ? `Stripe could not be reached: ${err.message}`
        : 'Something went wrong reaching Stripe. Try again in a moment.';

  const checkout = useMutation({
    mutationFn: (quantity: number) => api.startCheckout(orgId, quantity),
    onMutate: () => setBillingError(null),
    onSuccess: (res) =>
      res
        ? window.location.assign(res.url)
        : setBillingError('Stripe returned no checkout page. Nothing has been charged.'),
    onError: (err) => setBillingError(explain(err)),
  });

  const portal = useMutation({
    mutationFn: () => api.billingPortal(orgId),
    onMutate: () => setBillingError(null),
    onSuccess: (res) =>
      res ? window.location.assign(res.url) : setBillingError('Stripe returned no billing page.'),
    onError: (err) => setBillingError(explain(err)),
  });

  /**
   * One self-contained two-pane glass panel (`docs/design-dashboard.md` §8b):
   * the category list on the left, the selected category's content on the right.
   * It is deliberately NOT a second app-level rail — the one rail is the shell's,
   * and this second level lives *inside* the floating panel. Settings changes one
   * thing at a time, so only the chosen category is on screen, which also keeps
   * this dense screen from being a single endless scroll.
   */
  const evidence = ent.data?.evidence === true;
  // Locations is no longer a Settings category — it is its own rail directory
  // (locked IA 2026-09-07). Settings is org-level config plus the records hub.
  const categories: { id: string; label: string }[] = [
    { id: 'records', label: 'Records' },
    { id: 'billing', label: 'Billing' },
    // Ownership is org/account config, so it lives here (owner correction
    // 2026-09-07) — moved off the People screen, which is now the drill only.
    { id: 'ownership', label: 'Ownership' },
    // Getting the organisation confirmed (migration 0150): what an unconfirmed
    // org's live alert reaches, and the request to lift that limit.
    { id: 'confirmation', label: 'Confirmation' },
    { id: 'golive', label: 'Going live' },
    { id: 'integrations', label: 'Integrations' },
    { id: 'languages', label: 'Alert languages' },
    { id: 'security', label: 'Security' },
    { id: 'templates', label: 'Message templates' },
    // The person's own WORK side for this org — name, email, photo (0166) and
    // work mobile (0138). The dashboard's stand-in for a Profile; the personal
    // side lives in the app and is never shown here.
    { id: 'you', label: 'You at work' },
  ];
  // Opens on Records, the first category: Settings exists as a hub to hold the
  // *relocated records* (the Readiness report and the Activity trail moved off
  // the rail into here), so the records hub is the landing that explains why this
  // screen is where it is. (settings-hub.test.tsx pins Records-on-open.)
  //
  // A deep link overrides that landing: the chrome's "Finish setup" banner lands
  // here at `#golive-<step>`, so Settings opens on the Going-live checklist at
  // the next outstanding step rather than on Records (owner fix 2026-09-07). The
  // hash is validated against the real category ids, so an unknown fragment
  // falls back to Records.
  const hashTarget = readSettingsHashTarget();
  const [category, setCategory] = useState<string>(() =>
    hashTarget.category && categories.some((c) => c.id === hashTarget.category)
      ? hashTarget.category
      : 'records',
  );

  return (
    <>
      <PageTitle sub={orgName}>Settings</PageTitle>

      <div className="flex flex-col gap-6 md:flex-row md:gap-8">
        {/* The category list — the second level, inside the panel, never a
            second rail. A hairline separates it from the content on desktop. */}
        <nav
          aria-label="Settings categories"
          className="md:w-[196px] md:shrink-0 md:border-r md:border-[var(--line)] md:pr-4"
        >
          <ul className="m-0 flex list-none flex-wrap gap-1 p-0 md:flex-col md:flex-nowrap">
            {categories.map((c) => {
              const active = c.id === category;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setCategory(c.id)}
                    aria-current={active ? 'true' : undefined}
                    className={`h-9 w-full rounded-[var(--radius-1)] px-3 text-left text-[14px] whitespace-nowrap transition-colors ${
                      active
                        ? 'bg-[var(--paper-3)] font-medium text-[var(--ink)]'
                        : 'text-[var(--ink-2)] hover:bg-[var(--paper-3)] hover:text-[var(--ink)]'
                    }`}
                  >
                    {c.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="min-w-0 flex-1">
          {category === 'records' ? (
            <>
              {/*
                The relocated records. The readiness report and the Activity
                record are one tap from here; the evidence workspace appears only
                for an org that actually has it — the DOJ NSW pilot does, an
                ordinary org never sees it (`entitlements.evidence`).
              */}
              <Section title="Records">
                <ListRow
                  onClick={() => onNavigate('readiness')}
                  label="Open the readiness report — every building and every gap"
                  trailing="Every building, every gap"
                >
                  Readiness
                </ListRow>
                <ListRow
                  onClick={() => onNavigate('activity')}
                  label="Open the activity record — drills, alerts and signed records"
                  trailing="Drills, alerts and signed records"
                >
                  Activity
                </ListRow>
              </Section>

              {/*
                Only a positive evidence:false hides the workspace. If the plan
                could not be loaded, do NOT silently pretend the org has no
                evidence — say so and offer a retry, so a DOJ operator whose
                entitlements call blipped is not quietly cut off.
              */}
              {ent.data?.evidence === false ? null : ent.isError ? (
                <Section title="Evidence">
                  <ListRow
                    onClick={() => void ent.refetch()}
                    label="Retry loading your plan"
                    trailing="Tap to try again"
                  >
                    Couldn’t load your plan
                  </ListRow>
                </Section>
              ) : evidence ? (
                <Section title="Evidence">
                  <p className="mb-2 max-w-[640px] text-[13px] text-[var(--ink-2)]">
                    The court-ready evidence workspace for this organisation.
                  </p>
                  <ListRow
                    onClick={() => onNavigate('evidence')}
                    label="Open the evidence inbox"
                    trailing="What is held, and its custody record"
                  >
                    Inbox
                  </ListRow>
                  <ListRow
                    onClick={() => onNavigate('cases')}
                    label="Open evidence cases"
                    trailing="Group evidence into cases"
                  >
                    Cases
                  </ListRow>
                  <ListRow
                    onClick={() => onNavigate('disclosure')}
                    label="Open disclosure"
                    trailing="Hand a bundle to someone who trusts neither party"
                  >
                    Disclosure
                  </ListRow>
                  <ListRow
                    onClick={() => onNavigate('redaction')}
                    label="Open redaction"
                    trailing="Redact before disclosure"
                  >
                    Redaction
                  </ListRow>
                  <ListRow
                    onClick={() => onNavigate('capture')}
                    label="Open capture"
                    trailing="Record an incident and ingest it as evidence"
                  >
                    Capture
                  </ListRow>
                </Section>
              ) : null}
            </>
          ) : null}

          {category === 'billing' ? (
            <Section title="Plan">
              {ent.isLoading || !ent.data ? (
                <Spinner />
              ) : (
                // The plan-and-billing box: a bounded, resting-elevation tile of a
                // handful of dissimilar figures, not an open section (ui.tsx Card,
                // §7.1 — "a plan-and-billing box is a Card"). The Section heading
                // above keeps the page's heading order intact.
                <Card>
                  <Metrics>
                    <Metric label="Plan" value={ent.data.plan} />
                    {/* Buildings are not metered (migration 0044), so a
                  used-against-allowance reading would assert a limit that no
                  longer exists. */}
                    <Metric label="Buildings" value={ent.data.sitesUsed} />
                    {/*
                People who work here, never the people they look after. A
                school sees its teachers, not its students, because that is
                what is metered (docs/billing.md).
              */}
                    <Metric
                      label="People who work here"
                      value={
                        ent.data.seatCommitment
                          ? `${ent.data.seatsUsed} / ${ent.data.seatCommitment}`
                          : String(ent.data.seatsUsed)
                      }
                    />
                    <Metric
                      label="Billing"
                      value={ent.data.state}
                      tone={ent.data.state === 'active' ? 'safe' : 'attention'}
                    />
                  </Metrics>

                  {/*
              The rule the whole billing design exists to protect, stated where
              an admin with a failed card will actually read it.
            */}
                  {/*
              Over the committed seats: a sentence, and nothing else. No
              button, no warning tone, nothing to resolve. Adding people is
              always allowed and always instant — the count is reconciled at
              renewal, and anything here that felt like a blocker would be
              the thing that gets somebody left off the list.
            */}
                  {ent.data.seatCommitment !== null &&
                  ent.data.seatsUsed > ent.data.seatCommitment ? (
                    <p className="mt-6 text-[13px] text-[var(--ink-2)]">
                      {ent.data.seatsUsed} people work here against {ent.data.seatCommitment} seats
                      committed. Nothing is limited by this — keep adding people as you need to, and
                      the count is reconciled at renewal.
                    </p>
                  ) : null}

                  {ent.data.state === 'past_due' ? (
                    <div className="mt-6">
                      <Finding>
                        A payment failed. Alerting is unaffected — every site stays live and every
                        drill still runs. What is paused is administration: adding a site, and
                        generating a new signed report.
                      </Finding>
                    </div>
                  ) : null}

                  {billingError ? (
                    <div className="mt-6">
                      <Finding>{billingError}</Finding>
                    </div>
                  ) : null}

                  <div className="mt-6 flex flex-wrap gap-3">
                    {ent.data.plan === 'free' ? (
                      <Button
                        variant="primary"
                        disabled={checkout.isPending}
                        onClick={() => checkout.mutate(Math.max(1, ent.data!.sitesUsed))}
                      >
                        Upgrade to Pro
                      </Button>
                    ) : null}
                    <Button disabled={portal.isPending} onClick={() => portal.mutate()}>
                      Manage billing
                    </Button>
                  </div>

                  {/*
              Rewritten 2026-08-25: this still said "the meter is sites, never
              people" a day after buildings stopped being metered. Copy that
              describes the old commercial model is worse than no copy — an
              admin reads it and plans around it.
            */}
                  <p className="mt-4 max-w-prose text-[14px] text-[var(--ink-2)]">
                    You pay for the people who work here, and never for the people you look after —
                    students, patients, visitors and followers are never counted. Every building is
                    included on every plan, and everyone in them is alerted on every plan.
                  </p>
                </Card>
              )}
            </Section>
          ) : null}

          {category === 'ownership' ? <TransferOwnership orgId={orgId} /> : null}

          {category === 'confirmation' ? (
            <OrgConfirmation
              orgId={orgId}
              orgName={orgName}
              onInvite={() => onNavigate('people')}
            />
          ) : null}

          {category === 'golive' ? (
            <GoingLive
              orgId={orgId}
              focusStep={hashTarget.category === 'golive' ? hashTarget.step : null}
            />
          ) : null}

          {category === 'integrations' ? (
            <>
              <Channels orgId={orgId} />
              <ScimSetup orgId={orgId} />
            </>
          ) : null}

          {category === 'languages' ? <Languages orgId={orgId} /> : null}

          {category === 'security' ? (
            <>
              <Section title="Security">
                <div className="text-[14px] text-[var(--ink-2)]">
                  <Status tone="safe">Roles are read on every request</Status>
                  <p className="mt-2 max-w-prose">
                    Removing somebody takes effect on their next action rather than when a token
                    expires.
                  </p>
                </div>
              </Section>
              <AuditTrail orgId={orgId} />
            </>
          ) : null}

          {category === 'templates' ? <Templates orgId={orgId} /> : null}

          {category === 'you' ? (
            <>
              <YourWorkProfile orgId={orgId} orgName={orgName} />
              <YourWorkMobile orgId={orgId} orgName={orgName} />
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}

/** Read a picked image as base64 (no data: prefix). */
function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('could not read the file'));
    reader.readAsDataURL(file);
  });
}

/**
 * What THIS organisation knows the signed-in person as (migration 0166,
 * docs/account-modes.md): the work name (the person's choice, else the
 * directory's), the work email, and the work photo. Only this organisation
 * sees these; the personal name, email and photo are never shown on any
 * dashboard. With no work photo the organisation sees none at all.
 */
function YourWorkProfile({ orgId, orgName }: { orgId: string; orgName: string }) {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.getMe() });
  const work = me.data?.memberships.find((m) => m.org_id === orgId)?.work;
  const [name, setName] = useState<string | null>(null);
  const done = () => void qc.invalidateQueries({ queryKey: ['me'] });
  const saveName = useMutation({
    mutationFn: (value: string | null) => api.setWorkName(orgId, value),
    onSuccess: () => {
      setName(null);
      done();
    },
  });
  const savePhoto = useMutation({
    mutationFn: async (file: File) => api.setWorkPhoto(orgId, await readBase64(file), file.type),
    onSuccess: done,
  });
  const removePhoto = useMutation({
    mutationFn: () => api.removeWorkPhoto(orgId),
    onSuccess: done,
  });

  if (!work) return null;
  const editing = name ?? work.nameChosen ?? '';
  const shown = work.name ?? work.email ?? 'your email address';

  return (
    <Section title={`You at ${orgName}`}>
      <p className="mb-4 max-w-prose text-[14px] text-[var(--ink-2)]">
        This is what {orgName} sees: on People, the live view, warden boards and the signed report.
        Your family and anyone you help see your personal profile in the app instead — never this.
      </p>
      <form
        className="max-w-[360px] space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          const value = editing.trim();
          saveName.mutate(value.length > 0 ? value : null);
        }}
      >
        <Field
          label="Your name here"
          value={editing}
          onChange={setName}
          placeholder={work.directoryName ?? ''}
          hint={
            work.directoryName
              ? `Leave it empty to use the name from ${orgName}'s directory: ${work.directoryName}.`
              : `${orgName} currently sees: ${shown}.`
          }
        />
        <Button type="submit" variant="primary" disabled={saveName.isPending || name === null}>
          {saveName.isPending ? 'Saving…' : 'Save name'}
        </Button>
        {saveName.isError ? <Finding>{api.errorMessage(saveName.error)}</Finding> : null}
      </form>
      <dl className="mt-5 grid max-w-[480px] grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-[14px]">
        <dt className="text-[var(--ink-2)]">Work email</dt>
        <dd className="text-[var(--ink)]">{work.email ?? 'None'}</dd>
      </dl>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        {work.photoUrl ? (
          <img
            src={work.photoUrl}
            alt={`Your photo at ${orgName}`}
            className="h-12 w-12 rounded-full object-cover"
          />
        ) : (
          <span className="text-[14px] text-[var(--ink-2)]">
            No work photo — {orgName} sees none.
          </span>
        )}
        <label className="cursor-pointer text-[14px] font-medium text-[var(--accent)] underline">
          {work.photoUrl ? 'Change photo' : 'Add a work photo'}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="sr-only"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) savePhoto.mutate(file);
              e.target.value = '';
            }}
          />
        </label>
        {work.photoUrl ? (
          <Button variant="quiet" onClick={() => removePhoto.mutate()}>
            Remove photo
          </Button>
        ) : null}
        {savePhoto.isPending ? <Spinner /> : null}
        {savePhoto.isError ? <Finding>{api.errorMessage(savePhoto.error)}</Finding> : null}
        {removePhoto.isError ? <Finding>{api.errorMessage(removePhoto.error)}</Finding> : null}
      </div>
    </Section>
  );
}

/**
 * The signed-in person's own work mobile for this organisation (migration
 * 0138): the number other administrators call from the authorise sheet.
 * Changing it needs a new texted code — a number counts as theirs only once
 * they have proved they hold it.
 */
function YourWorkMobile({ orgId, orgName }: { orgId: string; orgName: string }) {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.getMe() });
  const [editing, setEditing] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const membership = me.data?.memberships.find((m) => m.org_id === orgId);
  const current = membership?.work_mobile ?? null;
  const remove = useMutation({
    mutationFn: () => api.removeOwnWorkMobile([orgId]),
    onSuccess: () => {
      setConfirmRemove(false);
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });

  return (
    <Section title="Your work mobile">
      <p className="mb-4 max-w-prose text-[14px] text-[var(--ink-2)]">
        The number {orgName} can call you on during an alert. {workMobileHelp(membership?.role)}
      </p>
      {current && !editing ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[15px] font-medium text-[var(--ink)]">{current.display}</span>
          <Status tone={current.verified ? 'safe' : 'attention'}>
            {current.verified ? 'Verified' : 'Not verified'}
          </Status>
          <Button onClick={() => setEditing(true)}>{current.verified ? 'Change' : 'Verify'}</Button>
          {confirmRemove ? (
            <>
              <span className="text-[14px] text-[var(--ink-2)]">
                {membership?.work_mobile_required
                  ? 'Nobody authorising your alerts could call you, and you will be asked for a number again.'
                  : 'Nobody authorising your alerts could call you.'}
              </span>
              <Button variant="primary" danger onClick={() => remove.mutate()}>
                {remove.isPending ? 'Removing…' : 'Remove it'}
              </Button>
              <Button variant="quiet" onClick={() => setConfirmRemove(false)}>
                Keep it
              </Button>
            </>
          ) : (
            <Button variant="quiet" onClick={() => setConfirmRemove(true)}>
              Remove
            </Button>
          )}
          {remove.isError ? <Finding>{api.errorMessage(remove.error)}</Finding> : null}
        </div>
      ) : (
        <div className="max-w-[360px]">
          <WorkMobileVerify
            countryCode={membership?.org_country ?? 'AU'}
            role={membership?.role}
            orgIds={[orgId]}
            initialPhone={current && !current.verified ? current.display : ''}
            verifyAvailable={me.data?.workMobile?.verifyAvailable !== false}
            onDone={() => setEditing(false)}
          />
          {current ? (
            <div className="mt-2">
              <Button variant="quiet" onClick={() => setEditing(false)}>
                Keep {current.display}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Section>
  );
}

/**
 * Transfer ownership — moved here from the People screen (owner correction
 * 2026-09-07): it is org/account config, a decision about the org's one Owner
 * seat, not roster maintenance. Behaviour is unchanged from where it lived on
 * People: there is exactly one Owner, transferring promotes the chosen person to
 * Owner and demotes the current Owner to Administrator, and it can only pass to
 * an existing Administrator. Only the current Owner sees a functioning form;
 * everybody else sees the read-only explanation.
 */
function TransferOwnership({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.getMe() });
  const people = useQuery({
    queryKey: ['people', orgId, {}],
    queryFn: () => api.getPeople(orgId, {}),
  });
  const [target, setTarget] = useState<string>('');
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const transfer = useMutation({
    mutationFn: (toUserId: string) => api.transferOwnership(orgId, toUserId),
    onSuccess: () => {
      setNote('Ownership transferred.');
      setConfirming(false);
      setTarget('');
      void qc.invalidateQueries({ queryKey: ['people', orgId] });
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (e: Error) => setNote(e.message),
  });

  if (me.isLoading || !me.data || people.isLoading || !people.data) return <Spinner />;

  // The membership for the org actually on screen — never `memberships[0]`.
  const myMembership = me.data.memberships.find((m) => m.org_id === orgId);
  const members = people.data.members;

  return (
    <Section title="Transfer ownership">
      <p className="mb-4 max-w-[560px] text-[var(--ink-2)]">
        There is exactly one Owner per organisation. Transferring the seat makes the person you pick
        the new Owner, and demotes the current Owner to Administrator. The new Owner takes over
        billing and can appoint or remove any admin.
      </p>

      {note ? (
        <div className="mb-4">
          <Finding tone="neutral">{note}</Finding>
        </div>
      ) : null}

      {!myMembership || myMembership.role !== 'owner' ? (
        <Empty>
          Only the current Owner can transfer ownership. If you need the seat moved, ask the Owner
          to do it here.
        </Empty>
      ) : (
        (() => {
          const candidates = members.filter(
            (m) => !m.deprovisioned_at && m.role === 'admin' && m.user_id !== me.data!.userId,
          );
          const targetPerson = candidates.find((c) => c.user_id === target);
          if (candidates.length === 0)
            return (
              <Empty>
                Ownership can only pass to another Administrator. Invite or promote somebody on the
                People screen before transferring.
              </Empty>
            );
          if (confirming && targetPerson)
            return (
              <div className="flex flex-wrap items-end gap-3">
                <p className="text-[var(--ink)]">
                  Transfer ownership to <strong>{targetPerson.label}</strong>? You cannot undo this
                  yourself — only they can hand it back.
                </p>
                <Button variant="primary" onClick={() => transfer.mutate(target)}>
                  Transfer ownership
                </Button>
                <Button variant="quiet" onClick={() => setConfirming(false)}>
                  Cancel
                </Button>
              </div>
            );
          return (
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-[320px]">
                <Select
                  label="New Owner"
                  value={target}
                  onChange={setTarget}
                  options={[
                    { value: '', label: 'Choose an administrator…' },
                    ...candidates.map((c) => ({ value: c.user_id, label: c.label })),
                  ]}
                />
              </div>
              <Button variant="quiet" disabled={!target} onClick={() => setConfirming(true)}>
                Transfer…
              </Button>
            </div>
          );
        })()
      )}
    </Section>
  );
}

/**
 * Mint a SCIM bearer token for automated provisioning.
 *
 * `docs/identity-provisioning.md`: SSO is free on every tier, SCIM
 * automated provisioning is Pro. The endpoint is at
 * `/orgs/:orgId/scim-token` and until now had no UI — a Pro customer
 * connecting Entra had to reach a Duress engineer to get their token.
 *
 * The token is shown *once*. The server writes only its hash, and the
 * response is the only place the plaintext will ever appear, so the
 * card says so out loud and offers a one-click copy. A refused free-
 * tier org gets a plain-English 402 explaining what needs upgrading.
 */
function ScimSetup({ orgId }: { orgId: string }) {
  const [minted, setMinted] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mint = useMutation({
    mutationFn: () => api.mintScimToken(orgId),
    onSuccess: (res) => {
      if (!res) return;
      setMinted(res.token);
      setError(null);
    },
    onError: (err: Error) => {
      if (err instanceof api.ApiError && err.status === 402) {
        setError('SCIM provisioning is a Pro feature. Upgrade the plan above to mint a token.');
      } else if (err instanceof api.ApiError && err.status === 403) {
        setError(
          'For your security this needs a fresh sign-in. Sign out and back in, then try again.',
        );
      } else {
        setError(err.message);
      }
    },
  });

  return (
    <Section title="SCIM provisioning (Entra, Okta, JumpCloud)">
      <p className="mb-3 max-w-[640px] text-[14px] text-[var(--ink-2)]">
        Let your identity provider push your people: a starter appears on the list the moment HR
        marks them onboarded, and a leaver comes off within seconds of being deprovisioned. Point
        your IdP at <Mono>{`${window.location.origin.replace('app.', 'api.')}/scim/v2`}</Mono> and
        paste the token below into its bearer field. Pro tier only.
      </p>

      {minted ? (
        <>
          <Finding tone="attention">
            This is the only time we will show this token. Copy it now — the server only keeps a
            hash. If you lose it, mint a new one and rotate it in your IdP.
          </Finding>
          <div className="mt-3">
            <Mono>{minted}</Mono>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              onClick={() =>
                void navigator.clipboard
                  .writeText(minted)
                  .then(() => setCopied(true))
                  .catch(() => setCopied(false))
              }
            >
              {copied ? 'Copied' : 'Copy token'}
            </Button>
            <Button variant="quiet" onClick={() => setMinted(null)}>
              Done
            </Button>
          </div>
        </>
      ) : (
        <>
          <Button variant="primary" disabled={mint.isPending} onClick={() => mint.mutate()}>
            {mint.isPending ? 'Minting…' : 'Mint a SCIM token'}
          </Button>
          {error ? (
            <div className="mt-3">
              <Finding>{error}</Finding>
            </div>
          ) : null}
        </>
      )}
    </Section>
  );
}

/**
 * The road to live alerts, as a checklist instead of a mid-emergency
 * discovery.
 *
 * An org qualifies for live, it is not switched on (migration 0011):stand-down
 * templates (R3), a completed drill, two admins, and a recorded evacuation
 * authority attested by a *different* admin. `GET /live/readiness` returns the
 * four conditions; this renders them as the steps they are, so the missing one
 * is found on a calm afternoon.
 */
/**
 * Exported so the readiness surfacing (correction-templates row and the
 * single-admin preview-and-retract line) can be tested directly without
 * mounting the whole Settings screen and its entitlement, template and
 * audit-trail queries.
 */
export function GoingLive({
  orgId,
  focusStep,
}: {
  orgId: string;
  /**
   * A going-live step id to scroll to and focus on mount — the chrome's "Finish
   * setup" banner deep-links here at the *next* outstanding step (owner fix
   * 2026-09-07), so the admin lands on the exact thing left to do rather than
   * the top of the checklist. One of the condition keys, or null for the plain
   * landing.
   */
  focusStep?: string | null;
}) {
  const qc = useQueryClient();
  const readiness = useQuery({
    queryKey: ['live-readiness', orgId],
    queryFn: () => api.getLiveReadiness(orgId),
  });
  const [authority, setAuthority] = useState('');
  const [note, setNote] = useState<string | null>(null);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['live-readiness', orgId] });

  const request = useMutation({
    mutationFn: () => api.requestLive(orgId, authority.trim()),
    onSuccess: () => {
      setNote('Recorded. A different administrator now attests it.');
      invalidate();
    },
    onError: (e: Error) => setNote(e.message),
  });

  const attest = useMutation({
    mutationFn: () => api.attestLive(orgId),
    onSuccess: () => {
      setNote(null);
      invalidate();
    },
    onError: (e: Error) => setNote(e.message),
  });

  if (readiness.isLoading || !readiness.data) {
    return (
      <Section title="Going live">
        <Spinner />
      </Section>
    );
  }

  const r = readiness.data;
  const c = r.conditions;

  return (
    <Section title="Going live">
      {r.live ? (
        <Finding tone="neutral">
          <Status tone="safe">Live alerts are enabled</Status>{' '}
          <span className="text-[var(--ink-2)]">
            {r.qualifiedScope === 'site_staff'
              ? '— for the people on site. A second admin and an attested authority unlock telling families and neighbours.'
              : '— this organisation can declare a real emergency.'}
          </span>
        </Finding>
      ) : (
        <p className="mb-4 max-w-[640px] text-[14px] text-[var(--ink-2)]">
          Four things stand between this organisation and the ability to declare a real emergency.
          Each is worth doing on a calm afternoon.
        </p>
      )}

      <div className="mt-4 space-y-2">
        <GoLiveStep id="allClearTemplates" focused={focusStep === 'allClearTemplates'}>
          <Status tone={c.allClearTemplates.ok ? 'safe' : 'attention'}>
            {c.allClearTemplates.ok
              ? `All-clear templates written (${c.allClearTemplates.eventTypes.join(', ')})`
              : 'No all-clear template yet — write one below, it is the message Hawaii took 38 minutes to compose'}
          </Status>
        </GoLiveStep>
        {/*
          The correction template as its own row rather than folded under
          the all-clear line (`docs/security.md` §R3, migration 0064). R3
          becomes the *primary* control on the preview-and-retract path:
          the one-tap retract sends the pre-drafted correction, so a
          single-admin org that owes one cannot exercise the safety it
          qualified on. Named apart because the action to close it is
          different (author a correction, not an all-clear) and because a
          conflated "templates" row hides that from the admin shopping
          around tier 2.
        */}
        <GoLiveStep id="correctionTemplates" focused={focusStep === 'correctionTemplates'}>
          <Status tone={c.correctionTemplates.ok ? 'safe' : 'attention'}>
            {c.correctionTemplates.ok
              ? `Correction templates written (${c.correctionTemplates.eventTypes.join(', ')})`
              : 'No correction template yet — the one-tap retract sends this, so on the preview-and-retract path it is required'}
          </Status>
        </GoLiveStep>
        <GoLiveStep id="drillCompleted" focused={focusStep === 'drillCompleted'}>
          <Status tone={c.drillCompleted.ok ? 'safe' : 'attention'}>
            {c.drillCompleted.ok
              ? `A drill has been run to completion (${new Date(
                  c.drillCompleted.lastClosedAt!,
                ).toLocaleDateString()})`
              : 'No drill has been run all the way to closed yet'}
          </Status>
        </GoLiveStep>
        <GoLiveStep id="twoAdmins" focused={focusStep === 'twoAdmins'}>
          <Status tone={c.twoAdmins.ok ? 'safe' : 'attention'}>
            {c.twoAdmins.ok
              ? `${c.twoAdmins.adminCount} administrators — a live alert beyond the site needs a second pair of eyes`
              : 'Only one administrator — alerts beyond the people on site need a second admin to authorise (R1)'}
          </Status>
        </GoLiveStep>
        {/*
          The tier-2 preview-and-retract mechanism named explicitly under
          R1's qualification (migration 0064, `docs/security.md` §1.1 tier
          2). An admin relying on the one-tap safety deserves to see the
          mechanism they are relying on rather than infer it from an admin
          count — the qualification path is a stored fact about the org
          (`live_single_admin_retract`), not a role lookup, and this line
          is what makes that visible.
        */}
        {r.singleAdminRetract ? (
          <p className="pl-4 text-[13px] leading-[18px] text-[var(--ink-2)]">
            This organisation qualifies to alert beyond staff on the preview-and-retract path. After
            sending you have <strong className="tabular">{r.retractWindowSeconds}s</strong> to
            retract, one tap, no second admin.
          </p>
        ) : null}
        <GoLiveStep id="authorityAttested" focused={focusStep === 'authorityAttested'}>
          <Status tone={c.authorityAttested.ok ? 'safe' : 'attention'}>
            {c.authorityAttested.ok
              ? `Evacuation authority recorded: ${c.authorityAttested.ecoAuthority}`
              : c.authorityAttested.ecoAuthority
                ? `Authority named (${c.authorityAttested.ecoAuthority}) — awaiting attestation by a different admin`
                : 'Nobody has recorded who holds the authority to order an evacuation'}
          </Status>
        </GoLiveStep>
      </div>

      {!c.correctionTemplates.ok ? (
        <p className="mt-3 max-w-[640px] text-[13px] leading-[18px] text-[var(--ink-2)]">
          Author correction template — pick “Correction” as the message kind in the templates
          section below. Composing the retraction during an incident is what took Hawaii 38 minutes.
        </p>
      ) : null}

      {!c.authorityAttested.ok ? (
        <div className="mt-5 max-w-[640px] border-t border-[var(--line)] pt-4">
          {!c.authorityAttested.ecoAuthority ? (
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-[340px]">
                <Field
                  label="Who holds the authority to order an evacuation"
                  value={authority}
                  onChange={setAuthority}
                  placeholder="The Principal, as Chief Warden under AS 3745"
                />
              </div>
              <Button
                variant="secondary"
                disabled={!authority.trim() || request.isPending}
                onClick={() => request.mutate()}
              >
                Record it
              </Button>
            </div>
          ) : (
            <Button
              variant="secondary"
              disabled={attest.isPending}
              label="Attest the recorded evacuation authority"
              onClick={() => attest.mutate()}
            >
              Attest — I confirm this authority is real
            </Button>
          )}
          {note ? (
            <div className="mt-3">
              <Finding tone="neutral">{note}</Finding>
            </div>
          ) : null}
        </div>
      ) : null}
    </Section>
  );
}

/**
 * One going-live checklist step, wrapped so the chrome's "Finish setup" deep
 * link can scroll to it and focus it (the `focused` one). Focus rather than a
 * bare anchor so a keyboard/screen-reader user lands *on* the step, not merely
 * near it; the ring makes it visible to a sighted user too. `scroll-mt` keeps it
 * clear of the sticky screen header when scrolled into view.
 */
function GoLiveStep({
  id,
  focused,
  children,
}: {
  id: string;
  focused: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!focused) return;
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.scrollIntoView?.({ block: 'center' });
  }, [focused]);
  return (
    <div
      ref={ref}
      id={`golive-${id}`}
      tabIndex={focused ? -1 : undefined}
      className={`scroll-mt-24 outline-none ${
        focused ? 'rounded-[var(--radius-1)] p-1 ring-2 ring-[var(--focus)]' : ''
      }`}
    >
      {children}
    </div>
  );
}

/**
 * The languages this organisation commits to alerting in.
 *
 * A commitment, not a preference (migration 0016): a declared language with
 * no all-clear template is one we cannot safely alert in (R3), and the
 * warning from the API is rendered exactly as it arrives.
 */
function Languages({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const org = useQuery({ queryKey: ['org', orgId], queryFn: () => api.getOrg(orgId) });
  const [draft, setDraft] = useState('');
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (locales: string[]) => api.updateOrg(orgId, { alertLocales: locales }),
    onSuccess: (res) => {
      if (!res) return;
      setWarnings(res.warnings);
      setError(null);
      void qc.invalidateQueries({ queryKey: ['org', orgId] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const declared = org.data?.alert_locales ?? [];

  return (
    <Section title="Alert languages">
      <p className="mb-4 max-w-[640px] text-[14px] text-[var(--ink-2)]">
        An instruction somebody cannot read did not reach them — they never confirm, and the
        accountability number is wrong. Declaring a language commits this organisation to holding
        templates in it.
      </p>

      {org.isLoading ? (
        <Spinner />
      ) : (
        <>
          {declared.length > 0 ? (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              {declared.map((l) => (
                <span
                  key={l}
                  className="inline-flex items-center gap-2 rounded-[var(--radius-1)] border border-[var(--line)] px-2 py-1 text-[13px]"
                >
                  <span className="mono">{l}</span>
                  <Button
                    variant="quiet"
                    label={`Stop alerting in ${l}`}
                    onClick={() => save.mutate(declared.filter((x) => x !== l))}
                  >
                    Remove
                  </Button>
                </span>
              ))}
            </div>
          ) : (
            <Empty>Only the default language. Add the languages spoken at your sites.</Empty>
          )}

          <div className="mt-4 flex items-end gap-3">
            <div className="w-[160px]">
              <Field label="Add a language" value={draft} onChange={setDraft} placeholder="ar" />
            </div>
            <Button
              variant="secondary"
              disabled={!draft.trim() || save.isPending}
              label={`Declare ${draft || 'this language'}`}
              onClick={() => {
                save.mutate([...declared, draft.trim().toLowerCase()]);
                setDraft('');
              }}
            >
              Declare
            </Button>
          </div>

          {error ? (
            <div className="mt-3">
              <Finding>{error}</Finding>
            </div>
          ) : null}
          {warnings.map((w) => (
            <div key={w} className="mt-3">
              <Finding>{w}</Finding>
            </div>
          ))}
        </>
      )}
    </Section>
  );
}

/**
 * The administration ledger, newest first.
 *
 * R18 made this append-only from the first migration; this is the first screen
 * that reads it back. Actions render as they were recorded — the log is
 * evidence, and prettifying evidence is how it stops being trusted. The detail
 * column shows the ids involved, because "site.updated by Trav" without which
 * site answers nothing.
 */
function AuditTrail({ orgId }: { orgId: string }) {
  const audit = useQuery({ queryKey: ['audit', orgId], queryFn: () => api.getAudit(orgId) });

  return (
    <Section title="Audit trail">
      {audit.isLoading || !audit.data ? (
        <Spinner />
      ) : audit.data.entries.length === 0 ? (
        <Empty>Nothing has been changed yet.</Empty>
      ) : (
        <Table head={['When', 'Action', 'Who', 'Detail']}>
          {audit.data.entries.map((e) => (
            <Row key={e.id}>
              <Cell>{new Date(e.created_at).toLocaleString()}</Cell>
              <Cell>
                <span className="mono text-[13px]">{e.action}</span>
              </Cell>
              <Cell>{e.actor ?? '—'}</Cell>
              <Cell wide>
                <span className="mono text-[12px] text-[var(--ink-2)]">
                  {e.detail ? JSON.stringify(e.detail) : '—'}
                </span>
              </Cell>
            </Row>
          ))}
        </Table>
      )}
    </Section>
  );
}
